/**
 * EditableLabel — Click-to-edit label shared by GraphNodeHeader and StructuralNode.
 *
 * Supports optional double-click handler (e.g. to enter a subgraph).
 * When onDoubleClick is provided, single-click uses a short delay to
 * distinguish it from double-click.
 */

import { memo, useState, useEffect, useRef, useCallback } from 'react';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { useScope } from '@/ui/contexts/ScopeContext';
import { useClickOrDoubleClick } from '@/ui/hooks/useClickOrDoubleClick';

interface EditableLabelProps {
  nodeId: string;
  label?: string;
  /** Optional double-click handler (e.g. enter subgraph) */
  onDoubleClick?: () => void;
  /** Style overrides for the display text */
  style?: React.CSSProperties;
  /** Style overrides for the input field */
  inputStyle?: React.CSSProperties;
  /** Placeholder shown in input when empty (defaults to nodeId) */
  placeholder?: string;
}

function EditableLabelComponent({
  nodeId,
  label,
  onDoubleClick,
  style,
  inputStyle,
  placeholder,
}: EditableLabelProps) {
  const { instanceId } = useScope();
  const { setNodeLabel } = useRuntime();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const save = useCallback(() => {
    const trimmed = editValue.trim();
    setNodeLabel(instanceId, nodeId, trimmed || undefined);
    setIsEditing(false);
  }, [editValue, instanceId, nodeId, setNodeLabel]);

  const startEditing = useCallback(() => {
    setEditValue(label ?? '');
    setIsEditing(true);
  }, [label]);

  // When onDoubleClick is provided, use delayed click to distinguish
  const { handleClick: delayedClick, handleDoubleClick: handleDblClick } = useClickOrDoubleClick({
    onClick: startEditing,
    onDoubleClick: onDoubleClick ?? (() => {}),
  });

  if (isEditing) {
    return (
      <div style={style}>
        <input
          ref={inputRef}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={save}
          onKeyDown={e => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          placeholder={placeholder ?? nodeId}
          style={{
            background: 'transparent',
            border: 'none',
            borderBottom: '1px solid #6b7280',
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            padding: 0,
            outline: 'none',
            width: '100%',
            minWidth: 80,
            ...inputStyle,
          }}
        />
      </div>
    );
  }

  return (
    <div
      onClick={onDoubleClick ? delayedClick : startEditing}
      onDoubleClick={onDoubleClick ? handleDblClick : undefined}
      style={{
        cursor: onDoubleClick ? 'pointer' : 'text',
        ...style,
      }}
      title={onDoubleClick ? 'Double-click to enter subgraph' : 'Click to edit label'}
    >
      {label || nodeId}
    </div>
  );
}

export const EditableLabel = memo(EditableLabelComponent);
